"use client";

import { type ReactNode, type JSX } from "react";
import { useTenant, type BusinessType } from "@/components/TenantProvider";

/**
 * ============================================================
 *  LAYOUT REGISTRY — Pilih layout berdasarkan business_type
 *
 *  Di sinilah "ajaib" terjadi:
 *  - adara.localhost → pakai PhotographyLayout
 *  - vasalon.localhost → pakai SalonLayout
 *  - ipung.localhost → pakai PersonalLayout
 *  - karpung.localhost → pakai IoTLayout
 *
 *  Setiap layout punya: wrapper, navigasi, footer, dan
 *  komponen landing page yang BERBEDA TOTAL.
 * ============================================================
 */

/* ── Default Layout (fallback) ── */
function DefaultLayout({ children }: { children: ReactNode }) {
  return <div className="min-h-screen bg-light-bg dark:bg-dark-bg">{children}</div>;
}

/* ── Photography Layout: Dark, cinematic, full-bleed ── */
function PhotographyLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: "var(--color-tenant-bg, #000000)",
        color: "var(--color-tenant-text, #ffffff)",
      }}
    >
      {children}
    </div>
  );
}

/* ── Salon Layout: Light, soft, elegant ── */
function SalonLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: "var(--color-tenant-bg, #FFF5F7)",
        color: "var(--color-tenant-text, #1d1d1f)",
        fontFamily: "var(--font-tenant-body, 'Inter'), sans-serif",
      }}
    >
      {children}
    </div>
  );
}

/* ── Personal Layout: Clean, minimal, professional ── */
function PersonalLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: "var(--color-tenant-bg, #FFFFFF)",
        color: "var(--color-tenant-text, #1d1d1f)",
      }}
    >
      {children}
    </div>
  );
}

/* ── IoT Layout: Dark, data-dashboard feel ── */
function IoTLayout({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen font-mono"
      style={{
        backgroundColor: "var(--color-tenant-bg, #0F172A)",
        color: "var(--color-tenant-text, #e2e8f0)",
      }}
    >
      {children}
    </div>
  );
}

/* ── Registry Map ── */
const LAYOUT_MAP: Record<string, (props: { children: ReactNode }) => JSX.Element> = {
  photography: PhotographyLayout,
  salon: SalonLayout,
  personal: PersonalLayout,
  iot: IoTLayout,
  iot_agriculture: IoTLayout,
  default: DefaultLayout,
};

function getLayoutComponent(businessType: BusinessType, layoutPreference?: string) {
  // Prioritaskan layout dari settings.design.layout
  if (layoutPreference && LAYOUT_MAP[layoutPreference]) {
    return LAYOUT_MAP[layoutPreference];
  }
  // Fallback ke business_type
  if (LAYOUT_MAP[businessType]) {
    return LAYOUT_MAP[businessType];
  }
  return LAYOUT_MAP.default;
}

/* ───────────────────────────────────────────────
   Exported Component — dipakai di App.tsx
   ─────────────────────────────────────────────── */
export function TenantLayout({ children }: { children: ReactNode }) {
  const { tenant, loading } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg">
        <div
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: tenant.themeColor, borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  const LayoutComponent = getLayoutComponent(
    tenant.businessType,
    tenant.settings.layout,
  );

  return <LayoutComponent>{children}</LayoutComponent>;
}
